import { useAppQuery } from 'services/reactQuery/useAppQuery';
import StaffHTTPService from 'services/HTTPService/staff/StaffHTTPService';
import { JournalId } from 'types';
import { useAppMutation } from 'services/reactQuery/useAppMutation';
import { useSimpleNotification } from 'hooks/useSimpleNotification/useSimpleNotification';
import { commonTexts, journalTexts } from 'consts/text';
import { queryClient } from 'services/reactQuery/queryClient';
import { useCurrentInstitution } from 'jotaiAtoms/currentInstitution';
import { DateRangeFilterType } from 'hooks/useDateRangeFilterType/useDateRangeFilterType';

type GroupJournalTopicsPageNetworkManageProps = {
    journalId: JournalId;
    date: string;
    dateRangeFilterType: DateRangeFilterType;
    onTopicRemoveRequestFinish: () => void;
};

export const useGroupJournalTopicsPageNetworkManage = ({
    journalId,
    date,
    dateRangeFilterType,
    onTopicRemoveRequestFinish,
}: GroupJournalTopicsPageNetworkManageProps) => {
    const { id: institutionId } = useCurrentInstitution();

    const { displaySuccess, displayError } = useSimpleNotification();

    const {
        data: groupTopicsData,
        isInitialLoading: isGroupTopicsDataInitialLoading,
        isRefetching: isGroupTopicsDataRefetching,
        error: groupTopicsDataError,
    } = useAppQuery(
        'GROUP_TOPICS',
        [journalId, { date, dateRangeFilterType }],
        () =>
            dateRangeFilterType === 'WEEKLY'
                ? StaffHTTPService.groupTopics.getGroupTopicsByWeek(journalId, date)
                : StaffHTTPService.groupTopics.getGroupTopics(journalId, date),
        { retry: false },
    );

    const { mutate: removeGroupTopic, isLoading: isRemoveGroupTopicLoading } = useAppMutation(
        StaffHTTPService.groupTopics.removeGroupTopic,
        {
            key: ['REMOVE_GROUP_TOPIC'],
            onSuccess: async (_, variables) => {
                onTopicRemoveRequestFinish();
                await queryClient.invalidateQueries([
                    institutionId,
                    'GROUP_TOPIC',
                    variables.journalId,
                    variables.groupTopicId,
                ]);
                displaySuccess(
                    journalTexts.journalDetails.groupJournal.topics.topicRemoveSuccessNotification,
                );
            },
            onError: () => {
                onTopicRemoveRequestFinish();
                displayError(commonTexts.errorMessages.actionExecution);
            },
            invalidateQueryKeys: [['GROUP_TOPICS', journalId]],
        },
    );

    return {
        groupTopicsData,
        isGroupTopicsDataInitialLoading,
        isGroupTopicsDataRefetching,
        isRemoveGroupTopicLoading,
        groupTopicsDataError,
        removeGroupTopic,
    };
};
